import React, { useState } from 'react';
import {
  Box,
  Input,
  VStack,
  IconButton,
  InputGroup, 
  FormControl,
  FormLabel,
  FormErrorMessage,
  Button,
} from '@chakra-ui/react';
import { authEndpoints } from '../lib/api/authApiClient';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useModernToast } from './ModernToast';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';

interface RegistrationFormProps {
  onSuccess?: () => void;
}

const RegistrationForm = ({ onSuccess }: RegistrationFormProps) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useModernToast();
  // Email validation regex
  const isValidEmail = (email: string) => /.+@.+\..+/.test(email);

  // Password validation helper (copied from admin repo)
  function validatePassword(password: string) {
    if (!password) return '';
    const errors = [];
    if (password.length < 8) errors.push('• At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('• At least one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('• At least one lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('• At least one number');
    if (!/[^A-Za-z0-9]/.test(password)) errors.push('• At least one special character');
    return errors.length ? errors.join('\n') : '';
  }

  const passwordError = validatePassword(formData.password);
  const confirmPasswordError = formData.confirmPassword && formData.confirmPassword !== formData.password ? 'Passwords do not match' : '';
  const isFormValid = formData.email && formData.password && formData.confirmPassword && isValidEmail(formData.email) && !passwordError && !confirmPasswordError;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'email' ? value.toLowerCase() : value
    }));
    if (name === 'password') setPasswordTouched(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    if (formData.password !== formData.confirmPassword) {
      toast.error('Error', 'Passwords do not match');
      setIsLoading(false);
      return;
    }
    try {
      const response = await fetch(authEndpoints.register(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (data.error) {
        toast.error('Error', data.error);
        return;
      }
      if (onSuccess) {
        onSuccess();
      }
      toast.success('Success', 'Account created. Sign in to complete registration');
    } catch (error: unknown) {
      let description = 'Registration failed. Please try again.';
      if (error && typeof error === 'object' && 'response' in error && error.response && typeof error.response === 'object' && 'data' in error.response && error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        description = (error.response.data as { message?: string }).message || description;
      } else if (error instanceof Error) {
        description = error.message;
      }
      toast.error('Error', description);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      bg="black"
      p={8}
      rounded="lg"
      w="full"
      maxW="md"
      boxShadow="xl"
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing={4}>
          <FormControl isInvalid={!isValidEmail(formData.email) && !!formData.email} isRequired>
            <FormLabel color="white">Email (this will be your username)</FormLabel>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              bg="whiteAlpha.100"
              color="white"
              borderColor="whiteAlpha.300"
              _hover={{ borderColor: 'whiteAlpha.400' }}
              disabled={isLoading}
            />
            {!isValidEmail(formData.email) && formData.email && (
              <FormErrorMessage>Invalid email address</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!(passwordTouched && passwordError)} isRequired>
            <FormLabel color="white">Password</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={() => setPasswordTouched(true)}
                bg="whiteAlpha.100"
                color="white"
                borderColor="whiteAlpha.300"
                _hover={{ borderColor: 'whiteAlpha.400' }}
                disabled={isLoading}
              />
              <Box position="absolute" right={2} top="50%" transform="translateY(-50%)" zIndex={2}>
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  size="sm"
                  variant="ghost"
                  bg="transparent"
                  backgroundColor="transparent"
                  color="white"
                  _hover={{ bg: 'transparent', backgroundColor: 'transparent', color: 'gray.200' }}
                  _active={{ bg: 'transparent', backgroundColor: 'transparent' }}
                  _focus={{ boxShadow: 'none' }}
                  onClick={() => setShowPassword((show) => !show)}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </IconButton>
              </Box>
            </InputGroup>
            {passwordTouched && passwordError && (
              <FormErrorMessage>
                <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                  {passwordError.split('\n').map((line, idx) => (
                    <li key={idx} style={{ textAlign: "left" }}>{line}</li>
                  ))}
                </ul>
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!(formData.confirmPassword && confirmPasswordError)} isRequired>
            <FormLabel color="white">Confirm Password</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                bg="whiteAlpha.100"
                color="white"
                borderColor="whiteAlpha.300"
                _hover={{ borderColor: 'whiteAlpha.400' }}
                disabled={isLoading}
              />
              <Box position="absolute" right={2} top="50%" transform="translateY(-50%)" zIndex={2}>
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  size="sm"
                  variant="ghost"
                  bg="transparent"
                  backgroundColor="transparent"
                  color="white"
                  _hover={{ bg: 'transparent', backgroundColor: 'transparent', color: 'gray.200' }}
                  _active={{ bg: 'transparent', backgroundColor: 'transparent' }}
                  _focus={{ boxShadow: 'none' }}
                  onClick={() => setShowPassword((show) => !show)}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </IconButton>
              </Box>
            </InputGroup>
            {formData.confirmPassword && confirmPasswordError && (
              <FormErrorMessage>{confirmPasswordError}</FormErrorMessage>
            )}
          </FormControl>

          <Button
            type="submit"
            w="full"
            mt={4}
            isLoading={isLoading}
            color="white"
            variant="dominus"
            disabled={!isFormValid || isLoading}
          >
            {isLoading ? "Registering..." : "Register"}
          </Button>
        </VStack>
      </form>
    </Box>
  );
};

export default RegistrationForm; 